import { useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { SignInContext } from '@modules/users';
import { useGetBlob } from '@modules/documents/api';
import {
  createFrontendRuntime,
  type TGetBlob,
  type TVersionHeadingsMap,
} from '@modules/documents/components/changesRuntime';
import DocumentContext from '@modules/documents/contexts/DocumentContext.context';
import flattenTreeHeadings from '@modules/documents/helpers/flattenTreeHeadings.helper';
import { getResolvedProseMirrorText } from '@modules/documents/helpers/resolvedProseMirrorText';
import { hasExplicitContentSelection } from '@modules/documents/tiptap/plugins/ExplicitContentSelection';
import { TEMPLATE_FIELD_INLINE_NAME } from '@modules/documents/doc-engine/templateFields';

import { AI_CONTENT_SELECTION_TEXT_MAX_CHARS } from '../constants';
import type { TAiContentSelection } from '../types';

export const useGetSelectedContent = () => {
  const documentContext = useContext(DocumentContext);
  const { mock } = useContext(SignInContext).organization;
  const queryClient = useQueryClient();
  const getBlob = useGetBlob();

  return async (): Promise<TAiContentSelection | null> => {
    const editor = documentContext?.editor;
    const { doc, version, versionId, treeHeadings, captionsMap } = documentContext ?? {};
    if (!editor || !doc || !version || !versionId || !hasExplicitContentSelection(editor)) return null;

    // Cell selections have one range per cell, with the head cell first.
    const ranges = editor.state.selection.ranges
      .map(({ $from, $to }) => ({ from: $from.pos, to: $to.pos }))
      .sort((a, b) => a.from - b.from);
    const seenElementIds = new Set<string>();
    ranges.forEach((range) => {
      editor.state.doc.nodesBetween(range.from, range.to, (node) => {
        const id = node.attrs?.id;
        // An inline field is an edit target of its own, which its paragraph is not in a bound document.
        if (id && (node.isTextblock || node.isAtom || node.type.name === TEMPLATE_FIELD_INLINE_NAME)) {
          seenElementIds.add(id);
        }
      });
    });
    const elementIds = [...seenElementIds];

    // Already too long without a single block resolved, so resolving them would only be discarded.
    const unresolvedText = ranges.map(({ from, to }) => editor.state.doc.textBetween(from, to, '\n')).join('\n');
    if (unresolvedText.length > AI_CONTENT_SELECTION_TEXT_MAX_CHARS) return { text: unresolvedText, elementIds };

    // The live tree covers the whole document; the saved list stands in until the editor builds it.
    const headings = treeHeadings?.length ? flattenTreeHeadings(treeHeadings) : version.headings;
    const versionHeadingsMap: TVersionHeadingsMap = {};
    if (doc.draft) versionHeadingsMap.draft = versionId === 'draft' ? headings : doc.draft.headings;
    for (const candidate of doc.versions) versionHeadingsMap[candidate.id] = candidate.headings;

    // The mounted block already fetched this blob through useBlob, which caches it under this key.
    const cachedGetBlob: TGetBlob = {
      mutateAsync: (variables) =>
        Promise.resolve(
          queryClient.getQueryData<string>([`blob-${variables.blobName}`]) ?? getBlob.mutateAsync(variables)
        ),
    };

    const options = {
      // No block-header renderer: the header links to the snippet and is not part of the content.
      runtime: createFrontendRuntime(
        doc,
        { version, headings, captions: captionsMap ?? {}, versionHeadingsMap },
        undefined,
        cachedGetBlob
      ),
      opts: { isH5RefEnabled: mock.isH5RefEnabled },
    };
    const text = (await Promise.all(ranges.map((range) => getResolvedProseMirrorText(editor, range, options)))).join(
      '\n'
    );

    return { text, elementIds };
  };
};
